import { CardContent, CardShell } from '@/components/cards/common';
import NoDataMessage from '@/components/states/NoDataMessage';
import { AgeChart } from '@/routes/AudiencePage/charts/AgeChart/index';
import { AgePlaysResponse, SelectedPlatform } from '@/types/api/age';
import { useEffect, useMemo, useState } from 'react';
import { useAgePlaysQuery } from '@/services/revenueApi';
import useFetchQuery from '@/hooks/useFetchQuery';
import { mixpanelTrack, RevenueDashboardWidget } from '@/utils';
import { MIXPANEL_EVENT } from '@/utils/mixpanelUtils/events';
import { getFirstDisplayPlatform } from '@/utils/platformUtils';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { AUDIENCE_TAB_EXPERIMENTAL_FEATURES_FLAG } from '@/utils/constants';
import AgeModal from './AgeModal';
import { ChevronRightRegularGlyph } from '@acast-tech/decibel-glyphs';
import { Button, Tooltip } from '@acast-tech/decibel';

const AgeCard = ({ className }: { className?: string }) => {
  const { queryResult } = useFetchQuery(useAgePlaysQuery, undefined, { sources: [] });

  const { data: agePlays } = queryResult;

  const platforms = useMemo(() => {
    return [...new Set(agePlays?.plays?.map((play) => play.platform) ?? [])];
  }, [agePlays]);

  type SelectedPlatform = (typeof platforms)[number];

  const [selectedPlatform, setSelectedPlatform] = useState<SelectedPlatform | null>(
    platforms?.[0] ?? null
  );

  const handlePlatformChange = (platform: SelectedPlatform) => {
    setSelectedPlatform(platform);

    mixpanelTrack(MIXPANEL_EVENT.audience.demographics.platformRadioSelected, {
      page: 'audience',
      widget: RevenueDashboardWidget.AgeDemographics,
      platform: platform,
      demographics: 'age',
    });
  };

  useEffect(() => {
    setSelectedPlatform(
      getFirstDisplayPlatform({ plays: platforms.map((p) => ({ platform: p })) })
    );
  }, [platforms]);

  return (
    <CardShell className={className} title="Age">
      <CardContent
        {...queryResult}
        renderData={(ageData: AgePlaysResponse) => (
          <Data
            data={ageData}
            selectedPlatform={selectedPlatform}
            handlePlatformChange={handlePlatformChange}
          />
        )}
      />
    </CardShell>
  );
};

const Data = ({
  data,
  selectedPlatform,
  handlePlatformChange,
}: {
  data: AgePlaysResponse;
  selectedPlatform: SelectedPlatform<typeof data> | null;
  handlePlatformChange: (platform: SelectedPlatform<typeof data>) => void;
}) => {
  const ldClient = useLDClient();
  const audienceTabExperimentalFeaturesEnabled =
    ldClient?.variation(AUDIENCE_TAB_EXPERIMENTAL_FEATURES_FLAG, false) ?? false;

  if (data?.plays?.length < 1 || selectedPlatform === null) {
    return <NoDataMessage dataType="age data" />;
  }

  return (
    <div>
      <AgeChart
        data={data}
        selectedPlatform={selectedPlatform}
        handlePlatformChange={handlePlatformChange}
      />
      {audienceTabExperimentalFeaturesEnabled ? (
        <AgeModal />
      ) : (
        <div className="flex flex-row justify-end">
          <Tooltip title="Coming soon" placement="bottom-end">
            <span>
              <Button
                className="self-right"
                variant="tertiary"
                disabled={true}
                glyphEnd={ChevronRightRegularGlyph}
              >
                More details
              </Button>
            </span>
          </Tooltip>
        </div>
      )}
    </div>
  );
};

export default AgeCard;
